// ---------------------------------------------------------------------------
//  src/waiter/WaiterPOSPage.js   –   versione con MODALE “Cucina o Bar?”
// ---------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

/* === ENDPOINTS =========================================================== */
const ENDPOINT_KITCHEN = "send_to_kitchen";
const ENDPOINT_BAR     = "send_to_waiter";

/* === PALETTE ============================================================== */
const palette = {
  primary:   "#FF7F00",
  highlight: "#FFF2E0",
  card:      "#FFFFFF",
  textDark:  "#333",
  modalBg:   "rgba(0,0,0,.5)",
  bar:       "#2ECC71",
  kitchen:   "#FF7F00",
};

/* ========================================================================= */
export default function WaiterPOSPage() {
  /* ---------- router params --------- */
  const { tableId, sessionId } = useParams();
  const navigate               = useNavigate();

  /* ---------- state principali ------ */
  const [table,         setTable]         = useState(null);
  const [menuItems,     setMenuItems]     = useState([]);
  const [orders,        setOrders]        = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  /* wizard */
  const [currentStep,   setCurrentStep]   = useState(1);

  /* filtri menu */
  const [categoryFilter, setCategoryFilter] = useState("");
  const [textFilter,     setTextFilter]     = useState("");
  const [allCategories,  setAllCategories]  = useState([]);

  /* commenti / espansione righe */
  const [expandedItemIds, setExpandedItemIds] = useState([]);
  const [commentEdits,    setCommentEdits]    = useState({});

  /* modale scelta destinazione ordine */
  const [showDestModal,   setShowDestModal]   = useState(false);

  /* ---------- LOAD ------------------------------------------------------- */
  useEffect(() => {
    fetchTable();
    fetchMenuItems();
    fetchOrdersForSession(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  /* ---------- API -------------------------------------------------------- */
  const fetchTable = async () => {
    try {
      const res = await api.get("/tables", { params: { table_id: tableId } });
      if (Array.isArray(res.data) && res.data.length) setTable(res.data[0]);
      else { alert("Tavolo non trovato"); navigate("/waiter"); }
    } catch {
      alert("Errore caricamento tavolo"); navigate("/waiter");
    }
  };

  const fetchMenuItems = async () => {
    try {
      const res = await api.get("/menu");
      setMenuItems(res.data);
      const cats = new Set(res.data.map((m) => m.category).filter(Boolean));
      setAllCategories([...cats]);
    } catch (err) {
      console.error("Errore menu:", err);
    }
  };

  const fetchOrdersForSession = async (table_session_id) => {
    try {
      const res  = await api.get("/orders", { params: { table_session_id } });
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.orders)
        ? res.data.orders
        : [];
      setOrders(list);
    } catch (err) {
      console.error("Errore ordini:", err);
    }
  };

  const refreshOrder = async (orderId) => {
    try {
      const res = await api.get("/orders", { params: { order_id: orderId } });
      if (Array.isArray(res.data) && res.data.length) {
        const upd = res.data[0].items ? res.data[0] : { ...res.data[0], items: [] };
        setOrders((prev) => prev.map((o) => (o.id === upd.id ? upd : o)));
        if (selectedOrder?.id === upd.id) setSelectedOrder(upd);
      }
    } catch (e) {
      console.error("refreshOrder:", e);
    }
  };

  /* ---------- CREAZIONE ORDINE ------------------------------------------ */
  const createOrder = async (isKitchen) => {
    try {
      const res = await api.post("/orders", {
        table_session_id: sessionId,
        status: "pending",
        is_kitchen_order: isKitchen,
      });
      const newOrd = res.data.items ? res.data : { ...res.data, items: [] };
      setSelectedOrder(newOrd);
      fetchOrdersForSession(sessionId);
      setCurrentStep(2);
    } catch (err) {
      console.error("Errore creazione ordine:", err?.response || err);
    } finally {
      setShowDestModal(false);
    }
  };

  /* ---------- ITEM HANDLERS (identici a prima) -------------------------- */
  const handleAddItem = async (menuItemId) => {
    if (!selectedOrder || selectedOrder.status !== "pending") return;
    const found = menuItems.find((m) => m.id === menuItemId);
    const fakeId = `tmp-${Date.now()}`;

    setSelectedOrder((prev) => {
      if (!prev) return prev;
      const exist = prev.items.find((it) => it.menu_item_id === menuItemId);
      if (exist)
        return {
          ...prev,
          items: prev.items.map((it) =>
            it.id === exist.id ? { ...it, quantity: it.quantity + 1 } : it
          ),
        };
      return {
        ...prev,
        items: [
          ...prev.items,
          { id: fakeId, menu_item_id: menuItemId, quantity: 1,
            menu_item_name: found?.name, menu_item_price: found?.price,
            status: "pending", comment: "" },
        ],
      };
    });

    try {
      const res = await api.post(`/orders/${selectedOrder.id}/items`, {
        order_id: selectedOrder.id,
        menu_item_id: menuItemId,
        quantity: 1,
        comment: "",
      });
      const created = res.data;
      setSelectedOrder((prev) => ({
        ...prev,
        items: prev.items.map((it) =>
          it.id === fakeId
            ? { ...created, menu_item_name: found?.name, menu_item_price: found?.price }
            : it
        ),
      }));
      refreshOrder(selectedOrder.id);
    } catch (e) {
      console.error("addItem:", e);
    }
  };

  const handleUpdateItemQuantity = async (itemId, newQty) => {
    if (!selectedOrder || selectedOrder.status !== "pending") return;
    setSelectedOrder((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.id === itemId ? { ...it, quantity: newQty } : it)),
    }));
    if (typeof itemId === "string" && itemId.startsWith("tmp-")) return;

    try {
      await api.patch(`/orders/${selectedOrder.id}/items/${itemId}`, { quantity: newQty });
      refreshOrder(selectedOrder.id);
    } catch (e) { console.error("updateQty:", e); }
  };

  const toggleExpand      = (id) => setExpandedItemIds((p) => p.includes(id) ? p.filter((i) => i!==id):[...p,id]);
  const handleCommentChange = (id, txt) => setCommentEdits((p) => ({ ...p, [id]: txt }));

  const handleSaveComment = async (itemId) => {
    if (!selectedOrder || selectedOrder.status !== "pending") return;
    try {
      await api.patch(`/orders/${selectedOrder.id}/items/${itemId}`, { comment: commentEdits[itemId] || "" });
      refreshOrder(selectedOrder.id);
      toggleExpand(itemId);
    } catch (e) { console.error("saveComment:", e); }
  };

  /* ---------- SEND ORDINE (cucina / bar) -------------------------------- */
  const handleSendOrder = async () => {
    if (!selectedOrder) return;
    const endpoint =
      selectedOrder.is_kitchen_order || selectedOrder.is_kitchen_order === undefined
        ? ENDPOINT_KITCHEN
        : ENDPOINT_BAR;
    try {
      await api.patch(`/orders/${selectedOrder.id}/${endpoint}`);
      setSelectedOrder((prev) =>
        prev ? { ...prev, status: "in_progress", sent_to_kitchen_at: new Date().toISOString() } : prev
      );
      refreshOrder(selectedOrder.id);
      fetchOrdersForSession(sessionId);
    } catch (e) {
      console.error("sendOrder:", e);
    }
  };

  /* ---------- UTILS ------------------------------------------------------ */
  const filteredMenu = menuItems.filter(
    (m) =>
      (!categoryFilter || m.category === categoryFilter) &&
      (!textFilter || m.name.toLowerCase().includes(textFilter.toLowerCase()))
  );
  const groupByCat = (items=[]) => items.reduce((a,it)=>{(a[it.menu_item_category||"Altri"]=a[it.menu_item_category||"Altri"]||[]).push(it);return a;}, {});
  const getTotal   = (o) => o?.items?.reduce((s,it)=>s+(it.menu_item_price||0)*it.quantity,0)||0;

  /* ---------- STEP 1 ----------------------------------------------------- */
  const renderStep1 = () => (
    <div style={styles.stepContainer}>
      <h3 style={styles.stepTitle}>1. Seleziona / Crea Ordine</h3>
      <div style={styles.ordersList}>
        {orders.map((o) => (
          <div key={o.id}
            style={{ ...styles.orderItem, backgroundColor:selectedOrder?.id===o.id?palette.highlight:palette.card }}
            onClick={() => setSelectedOrder(o.items?o:{...o,items:[]})}>
            <strong>Ordine #{o.id}</strong> – {o.status}
          </div>
        ))}
      </div>
      <button style={styles.createOrderButton} onClick={()=>setShowDestModal(true)}>
        + Nuovo Ordine
      </button>
    </div>
  );

  /* ---------- STEP 2 ----------------------------------------------------- */
  const renderStep2 = () => {
    if (!selectedOrder)
      return <EmptyStep step={2} msg="Seleziona prima un ordine (Step 1)." />;
    const canModify = selectedOrder.status==="pending";
    return (
      <div style={styles.stepContainer}>
        <h3 style={styles.stepTitle}>2. Menu</h3>
        {/* filtri */}
        <div style={styles.filterRow}>
          <select style={styles.select} value={categoryFilter} onChange={(e)=>setCategoryFilter(e.target.value)}>
            <option value="">Tutte le categorie</option>
            {allCategories.map((c)=><option key={c}>{c}</option>)}
          </select>
          <input style={styles.input} placeholder="Cerca…" value={textFilter} onChange={(e)=>setTextFilter(e.target.value)} />
        </div>
        {/* elenco piatti */}
        <div style={styles.menuList}>
          {filteredMenu.length===0 && <p>Nessun piatto trovato.</p>}
          {filteredMenu.map((mi)=>{
            const ex = selectedOrder.items.find((it)=>it.menu_item_id===mi.id);
            const qty=ex?ex.quantity:0; const id=ex?.id; const exp=expandedItemIds.includes(id);
            return (
              <div key={mi.id} style={styles.menuItemRow}>
                <div style={styles.menuItemHeader}>
                  <div style={styles.menuItemLeft}>
                    <strong>{mi.name}</strong>
                    {mi.category && <span style={styles.menuCat}>({mi.category})</span>} –{" "}
                    <span style={styles.price}>{mi.price?.toFixed(2)} €</span>
                  </div>
                  <div style={styles.menuItemRight}>
                    {canModify && ex && <button style={styles.commentBtn} onClick={()=>toggleExpand(id)}>📝</button>}
                    {canModify ? (
                      <>
                        <button style={styles.qtyBtn} disabled={qty<=0} onClick={()=>qty>0&&handleUpdateItemQuantity(id,qty-1)}>-</button>
                        <span style={styles.qtyLabel}>{qty}</span>
                        <button style={styles.qtyBtn} onClick={()=>ex?handleUpdateItemQuantity(id,qty+1):handleAddItem(mi.id)}>+</button>
                      </>
                    ):<span style={styles.info}>{qty>0?`Nell'ordine: ${qty}`:"—"}</span>}
                  </div>
                </div>
                {ex&&exp&&(
                  <div style={styles.commentArea}>
                    <label>Commento:</label>
                    <textarea rows={2} style={styles.commentTxt} value={commentEdits[id]??ex.comment??""} onChange={(e)=>handleCommentChange(id,e.target.value)} />
                    <button style={styles.saveCommentBtn} onClick={()=>handleSaveComment(id)}>Salva</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ---------- STEP 3 ----------------------------------------------------- */
  const renderStep3 = () => {
    if (!selectedOrder) return <EmptyStep step={3} msg="Nessun ordine selezionato." />;
    const canModify = selectedOrder.status==="pending";
    const grouped   = groupByCat(selectedOrder.items);
    return (
      <div style={styles.stepContainer}>
        <h3 style={styles.stepTitle}>3. Riepilogo Ordine</h3>
        <div style={styles.reviewBox}>
          {Object.keys(grouped).length===0 && <p>Nessun piatto.</p>}
          {Object.entries(grouped).map(([cat,arr])=>(
            <div key={cat} style={styles.catGroup}>
              <h4 style={styles.catTitle}>{cat}</h4>
              {arr.map((it)=>{
                const exp=expandedItemIds.includes(it.id);
                return (
                  <div key={it.id} style={styles.reviewItem}>
                    <div style={styles.flexRow}>
                      <span style={styles.expandIcon} onClick={()=>toggleExpand(it.id)}>{exp?"▼":"▶"}</span>
                      <span>{it.menu_item_name} × {it.quantity} ({it.status})</span>
                      {canModify&&(
                        <div style={styles.btnGroup}>
                          <button style={styles.qtyBtn} onClick={()=>it.quantity>1&&handleUpdateItemQuantity(it.id,it.quantity-1)}>-</button>
                          <button style={styles.qtyBtn} onClick={()=>handleUpdateItemQuantity(it.id,it.quantity+1)}>+</button>
                        </div>
                      )}
                    </div>
                    {it.menu_item_price!==undefined&&<span style={styles.price}>{(it.menu_item_price*it.quantity).toFixed(2)} €</span>}
                    {exp&&(
                      <div style={styles.commentAreaStep3}>
                        <label>Commento:</label>
                        <textarea rows={2} style={styles.commentTxt} value={commentEdits[it.id]??it.comment??""} disabled={!canModify} onChange={(e)=>handleCommentChange(it.id,e.target.value)} />
                        {canModify&&<button style={styles.saveCommentBtn} onClick={()=>handleSaveComment(it.id)}>Salva</button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          <div style={styles.orderTotal}>Totale: {getTotal(selectedOrder).toFixed(2)} €</div>
          {selectedOrder.status==="pending"
            ? <button style={styles.sendButton} onClick={handleSendOrder}>
                {selectedOrder.is_kitchen_order||selectedOrder.is_kitchen_order===undefined?"Invia in Cucina":"Invia al Bar"}
              </button>
            : <p style={styles.info}>Ordine in stato: <strong>{selectedOrder.status}</strong></p>}
        </div>
      </div>
    );
  };

  /* ---------- RENDER PRINCIPALE ----------------------------------------- */
  if (!table) return <div style={styles.loading}><p>Caricamento tavolo #{tableId}…</p></div>;

  return (
    <div style={styles.container}>

      {/* header ----------------------------------------------------------- */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={()=>navigate("/waiter")}>←</button>
        <h2 style={styles.headerTitle}>Tavolo {table.table_number} (Sessione #{sessionId})</h2>
      </div>

      {/* step indicator ---------------------------------------------------- */}
      <div style={styles.stepsNav}>
        {["Ordini","Menù","Riepilogo"].map((lbl,i)=>(
          <StepIndicator key={i} stepNumber={i+1} currentStep={currentStep} label={lbl} onClick={()=>setCurrentStep(i+1)} />
        ))}
      </div>

      {/* contenuto --------------------------------------------------------- */}
      <div style={styles.content}>
        {currentStep===1&&renderStep1()}
        {currentStep===2&&renderStep2()}
        {currentStep===3&&renderStep3()}
      </div>

      {/* footer nav -------------------------------------------------------- */}
      <div style={styles.footer}>
        {currentStep>1 && <button style={styles.navBtn} onClick={()=>setCurrentStep((p)=>p-1)}>Indietro</button>}
        {currentStep<3 && <button style={styles.navBtn} onClick={()=>setCurrentStep((p)=>p+1)}>Avanti</button>}
      </div>

      {/* --------- MODALE “cucina o bar?” ---------------------------------- */}
      {showDestModal && (
        <div style={styles.modalOverlay} onClick={()=>setShowDestModal(false)}>
          <div style={styles.modalBox} onClick={(e)=>e.stopPropagation()}>
            <h3 style={{marginTop:0}}>Dove deve essere preparato l’ordine?</h3>
            <div style={styles.modalButtons}>
              <button style={{...styles.destBtn, background:palette.kitchen}}
                      onClick={()=>createOrder(true)}>
                Cucina
              </button>
              <button style={{...styles.destBtn, background:palette.bar}}
                      onClick={()=>createOrder(false)}>
                Bar
              </button>
            </div>
            <button style={styles.modalClose} onClick={()=>setShowDestModal(false)}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* === COMPONENTI DI SUPPORTO ============================================= */
function StepIndicator({ stepNumber, currentStep, label, onClick }) {
  return (
    <div onClick={onClick}
      style={{ padding:"8px 12px", borderRadius:4, color:"#fff", fontWeight:"bold",
               cursor:"pointer", margin:"0 5px",
               background: stepNumber===currentStep?palette.primary:"#aaa" }}>
      {label}
    </div>
  );
}

function EmptyStep({ step, msg }) {
  return (
    <div style={styles.stepContainer}>
      <h3 style={styles.stepTitle}>{step}. </h3>
      <p style={styles.warning}>{msg}</p>
    </div>
  );
}

/* === STILI =============================================================== */
const styles = {
  container:{ display:"flex", flexDirection:"column", height:"100vh", background:"#FAFAFA", fontSize:18 },
  loading:  { display:"flex", justifyContent:"center", alignItems:"center", height:"100vh" },

  header:   { display:"flex", alignItems:"center", padding:10, background:"#fff", boxShadow:"0 2px 4px rgba(0,0,0,.1)" },
  backBtn:  { background:"none", border:"none", fontSize:22, marginRight:10, color:palette.primary, cursor:"pointer" },
  headerTitle:{ margin:0, fontSize:18, fontWeight:"bold", color:palette.textDark },

  stepsNav: { display:"flex", justifyContent:"center", padding:5, borderBottom:"1px solid #ddd", background:"#f9f9f9" },
  content:  { flex:1, overflowY:"auto", padding:10 },
  footer:   { display:"flex", justifyContent:"space-between", padding:10, borderTop:"1px solid #ddd", background:"#fff" },
  navBtn:   { background:palette.primary, color:"#fff", border:"none", borderRadius:4, padding:"12px 20px", fontWeight:"bold", cursor:"pointer" },

  /* step generic */
  stepContainer:{ marginBottom:20 },
  stepTitle:{ marginBottom:10, fontWeight:"bold", fontSize:18, color:palette.textDark },
  warning:{ color:"red", fontStyle:"italic" },

  /* step1 */
  ordersList:{ maxHeight:300, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 },
  orderItem:{ padding:8, border:"1px solid #ccc", borderRadius:4, cursor:"pointer" },
  createOrderButton:{ background:"#FDB813", border:"none", borderRadius:4, width:"100%", padding:12, fontWeight:"bold", cursor:"pointer" },

  /* step2 */
  filterRow:{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 },
  select:{ flex:1, padding:10, borderRadius:4, border:"1px solid #ccc" },
  input:{ flex:2, padding:10, borderRadius:4, border:"1px solid #ccc" },
  menuList:{ maxHeight:300, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 },
  menuItemRow:{ background:"#fff", border:"1px solid #ddd", borderRadius:4 },
  menuItemHeader:{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:8 },
  menuItemLeft:{ display:"flex", flexWrap:"wrap", gap:4 },
  menuCat:{ fontSize:14, color:"#666" },
  price:{ fontWeight:"bold", color:palette.primary },
  menuItemRight:{ display:"flex", alignItems:"center", gap:6 },
  qtyBtn:{ background:palette.primary, color:"#fff", border:"none", borderRadius:4, padding:"4px 10px", fontSize:16, cursor:"pointer" },
  qtyLabel:{ minWidth:28, textAlign:"center", fontWeight:"bold" },
  commentBtn:{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#444" },

  commentArea:{ background:"#f9f9f9", borderTop:"1px solid #ddd", padding:8 },
  commentTxt:{ width:"100%", padding:6, fontSize:16 },
  saveCommentBtn:{ background:palette.primary, color:"#fff", border:"none", borderRadius:4, padding:"6px 10px", cursor:"pointer", marginTop:4 },

  /* step3 */
  reviewBox:{ border:"1px solid #ddd", borderRadius:4, padding:10, background:"#fff" },
  catGroup:{ marginBottom:10 },
  catTitle:{ marginBottom:5, fontWeight:"bold" },
  reviewItem:{ borderBottom:"1px solid #ddd", paddingBottom:6, marginBottom:6 },
  flexRow:{ display:"flex", alignItems:"center", gap:10 },
  expandIcon:{ cursor:"pointer", fontSize:16, width:22 },
  btnGroup:{ display:"flex", gap:6 },
  commentAreaStep3:{ background:"#f9f9f9", borderTop:"1px solid #ddd", padding:8, marginTop:4 },

  orderTotal:{ textAlign:"right", fontWeight:"bold", fontSize:18, marginTop:10 },
  sendButton:{ background:palette.primary, color:"#fff", border:"none", borderRadius:4, padding:12, width:"100%", fontWeight:"bold", cursor:"pointer" },
  info:{ color:"#555", marginTop:6 },

  /* modal */
  modalOverlay:{ position:"fixed", top:0, left:0, right:0, bottom:0, background:palette.modalBg, display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000 },
  modalBox:{ background:"#fff", padding:20, borderRadius:8, width:260, textAlign:"center", boxShadow:"0 2px 10px rgba(0,0,0,.2)" },
  modalButtons:{ display:"flex", gap:10, marginTop:10, marginBottom:10 },
  destBtn:{ flex:1, border:"none", color:"#fff", padding:"12px 0", borderRadius:6, fontWeight:"bold", cursor:"pointer" },
  modalClose:{ background:"none", border:"none", color:"#777", cursor:"pointer", marginTop:4 },
};
